/*
 * @Author: Zhouqi
 * @Date: 2025-11-10 15:00:28
 * @LastEditors: Zhouqi
 * @LastEditTime: 2026-01-05 14:30:39
 */
import { FC, memo } from 'react';

import { useFileValidator } from '@/components/FileUpload/common/hooks/useFileValidator';
import { FileUploadRule } from '@/components/FileUpload/common/types/validator';
import { useTranslate } from '@/hooks/useTranslate';

import { ImageLibModal, VideoLibModal } from '../VideoLibModal.tsx';

interface LibrarySelectorProps {
  onConfirm?: (files: any[]) => void;
  className?: string;
  type?: number; // 素材库类型
  existFiles?: any[];
  maxCount?: number;
  rules?: FileUploadRule[]; // 文件校验规则
  accept?: string; // 文件类型限制
  onValidationFailed?: (error: string) => void; // 校验失败回调
}

const libraryModalMap: Record<
  number,
  { open: (onConfirm: (files: any[]) => void) => void }
> = {
  5: VideoLibModal,
  2: ImageLibModal,
};

const LibrarySelector: FC<LibrarySelectorProps> = (props) => {
  const {
    onConfirm,
    className,
    existFiles = [],
    maxCount,
    type = 5,
    rules,
    accept,
    onValidationFailed,
  } = props;
  const { t } = useTranslate();
  const { validate } = useFileValidator({ rules, fileList: existFiles, accept });

  const originalConfirm = async (files: any[]) => {
    if (maxCount && existFiles.length >= maxCount) {
      onValidationFailed?.(t('maxCount', { maxCount }));
      return;
    }

    // 检查数量限制
    const totalCount = existFiles.length + (files?.length || 0);
    if (maxCount && totalCount > maxCount) {
      onValidationFailed?.(
        t('maxCount-currentCount-allowCount', {
          maxCount: maxCount,
          currentCount: existFiles.length,
          allowCount: maxCount - existFiles.length,
        }),
      );
      return;
    }

    // 获取 URL 的文件扩展名
    const getFileExtension = (url: string): string => {
      const urlWithoutQuery = url.split('?')[0];
      const matches = urlWithoutQuery.match(/\.([^.]+)$/);
      return matches ? `.${matches[1]}` : '';
    };

    // 在一个循环中处理文件格式化和校验
    const formatFiles: any[] = [];
    for (const item of files || []) {
      try {
        const { title, fileUrl } = item;
        // 从文件 URL 获取文件内容
        const response = await fetch(fileUrl);
        const blob = await response.blob();

        // 根据 URL 后缀确定文件名（如果原文件名没有扩展名）
        const fileExtension = getFileExtension(fileUrl);
        const fileName = title.includes('.') ? title : `${title}${fileExtension}`;

        // 创建 File 对象用于校验
        const file = new File([blob], fileName, { type: blob.type });

        // 执行校验
        const validationResult = await validate(file);
        if (!validationResult.valid) {
          const errorMsg = validationResult.errors.join('; ');
          onValidationFailed?.(errorMsg);
          continue;
        }

        // 校验通过，格式化文件对象
        formatFiles.push({
          ...item,
          uid: fileUrl,
          name: title,
          url: fileUrl,
          status: 'done',
          originFileObj: file,
        });
      } catch (error) {
        const errorMsg = t('文件校验失败');
        onValidationFailed?.(errorMsg);
        return;
      }
    }

    const newFiles = [...existFiles, ...formatFiles];
    onConfirm?.(newFiles);
  };

  return (
    !!onConfirm && (
      <span
        className={`text-[12px] text-[#4931FA] underline cursor-pointer ${className ?? ''}`}
        onClick={(e) => {
          e.stopPropagation();
          libraryModalMap[type]?.open(originalConfirm);
        }}
      >
        {t('9c8b39d051c64ae60d0e2485a17ea993')}
      </span>
    )
  );
};

export default memo(LibrarySelector);
